import apiClient, { type ApiClientError } from './api';
import { interceptorLogger } from './logger';
import { AuthErrorHandler } from './auth-error-handler';

// Flag to track if we're validating an existing token
let isValidatingToken = false;

// Flag to track if we're in auto-login process
let isAutoLoginInProgress = false;

// Set validation flag
export function setTokenValidationState(validating: boolean) {
  isValidatingToken = validating;
}

// Set auto-login flag
export function setAutoLoginState(inProgress: boolean) {
  isAutoLoginInProgress = inProgress;
}

// Authentication error interceptor
export const authErrorInterceptor = {
  onError: async (error: ApiClientError) => {
    interceptorLogger.debug('Auth error caught:', {
      status: error.status,
      isAuthError: error.isAuthError(),
      isValidatingToken,
      isAutoLoginInProgress,
      pathname:
        typeof window !== 'undefined' ? window.location.pathname : 'server',
      message: error.message,
    });

    // Handle 401 errors with unified handler
    if (error.status === 401) {
      AuthErrorHandler.handle401(error, {
        isValidating: isValidatingToken,
        isAutoLogin: isAutoLoginInProgress,
        pathname:
          typeof window !== 'undefined' ? window.location.pathname : undefined,
      });
    }

    // Handle 403 errors
    if (error.status === 403) {
      AuthErrorHandler.handle403(error);
    }
  },
};

// Request logger interceptor (for development)
export const requestLoggerInterceptor = {
  onRequest: async (config: RequestInit & { url: string }) => {
    if (process.env.NODE_ENV === 'development') {
      interceptorLogger.debug(`${config.method || 'GET'} ${config.url}`);
      if (config.body) {
        interceptorLogger.debug(
          'Request Body:',
          JSON.parse(config.body as string)
        );
      }
    }
    return config;
  },
  onError: async (error: ApiClientError) => {
    if (process.env.NODE_ENV === 'development') {
      interceptorLogger.error(
        `API Error Status: ${error.status}`,
        error.apiError
      );
    }
  },
};

// Initialize interceptors
export function initializeInterceptors() {
  apiClient.addInterceptor(authErrorInterceptor);

  if (process.env.NODE_ENV === 'development') {
    apiClient.addInterceptor(requestLoggerInterceptor);
  }
}
